/**
 * Dependency Store
 * Manages dependency state using Zustand
 */

import { create } from 'zustand';
import { DependencyWithDetails, TaskDependencies } from '@/types';

interface DependencyState {
  // State
  taskDependencies: Map<string, TaskDependencies>;
  projectDependencies: DependencyWithDetails[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setTaskDependencies: (taskId: string, dependencies: TaskDependencies) => void;
  setProjectDependencies: (dependencies: DependencyWithDetails[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  addDependency: (taskId: string, dependency: DependencyWithDetails, isBlocking: boolean) => void;
  removeDependency: (taskId: string, dependencyId: string) => void;
  clearDependencies: () => void;
}

export const useDependencyStore = create<DependencyState>()((set) => ({
  // Initial state
  taskDependencies: new Map(),
  projectDependencies: [],
  isLoading: false,
  error: null,

  // Actions
  setTaskDependencies: (taskId, dependencies) =>
    set((state) => {
      const newMap = new Map(state.taskDependencies);
      newMap.set(taskId, dependencies);
      return { taskDependencies: newMap };
    }),

  setProjectDependencies: (dependencies) =>
    set({ projectDependencies: dependencies }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  addDependency: (taskId, dependency, isBlocking) =>
    set((state) => {
      const newMap = new Map(state.taskDependencies);
      const current = newMap.get(taskId) || { blocking: [], blockedBy: [] };

      if (isBlocking) {
        newMap.set(taskId, {
          ...current,
          blocking: [...current.blocking, dependency],
        });
      } else {
        newMap.set(taskId, {
          ...current,
          blockedBy: [...current.blockedBy, dependency],
        });
      }

      return { taskDependencies: newMap };
    }),

  removeDependency: (taskId, dependencyId) =>
    set((state) => {
      const newMap = new Map(state.taskDependencies);
      const current = newMap.get(taskId);

      if (current) {
        newMap.set(taskId, {
          blocking: current.blocking.filter((d) => d.dependencyId !== dependencyId),
          blockedBy: current.blockedBy.filter((d) => d.dependencyId !== dependencyId),
        });
      }

      return {
        taskDependencies: newMap,
        projectDependencies: state.projectDependencies.filter(
          (d) => d.dependencyId !== dependencyId
        ),
      };
    }),

  clearDependencies: () =>
    set({
      taskDependencies: new Map(),
      projectDependencies: [],
    }),
}));

// Selectors
export const useTaskDependencies = (taskId: string | undefined) =>
  useDependencyStore((state) =>
    taskId ? state.taskDependencies.get(taskId) : undefined
  );

export const useProjectDependencies = () =>
  useDependencyStore((state) => state.projectDependencies);

export const useDependencyLoading = () =>
  useDependencyStore((state) => state.isLoading);

export const useDependencyError = () =>
  useDependencyStore((state) => state.error);

export default useDependencyStore;
